import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Card,
    CardActionArea,
    CardContent,
    useTheme,
    useMediaQuery,
    Container,
    Grid,
    Chip,
    Divider,
    Fade,
    Slide,
    IconButton,
    Tooltip,
    Stack,
    Alert,
    Skeleton,
    Backdrop,
    CircularProgress
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    School as SchoolIcon,
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CalendarToday as CalendarIcon,
    Person as PersonIcon
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import UnitDeepDive from "./UnitDeepDive";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../../loadingIcons/LoadingIcons";

const UnitDetailAssessmentPage = () => {
    const { subjectId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [tableRows, setTableRows] = useState([]);
    const [studentName, setStudentName] = useState("");
    const [term, setTerm] = useState("");
    const [year, setYear] = useState("");
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const location = useLocation();
    const { studentId, fullName, pin, subject, subject_id, selectedTerm } = location.state || {};

    useEffect(() => {
        const loadData = () => {
            setLoading(true);
            try {
                const storedData = localStorage.getItem("student_dashboard_data");
                if (!storedData) {
                    setError("No data available");
                    setLoading(false);
                    return;
                }

                const parsed = JSON.parse(storedData);
                const cards = parsed.progress_cards || [];

                const filtered = cards.filter(
                    (c) => c.subject_id === parseInt(subject_id) && c.term_number === parseInt(selectedTerm)
                );

                const rows = filtered.map((card) => {
                    const achieved = card.entries
                        .filter((e) => e.is_achieved)
                        .map((e) => parseInt(e.dok_level));

                    const maxDOK = achieved.length > 0 ? Math.max(...achieved) : null;
                    const maxEntry = card.entries.find((e) => parseInt(e.dok_level) === maxDOK);

                    return {
                        unit: card.unit,
                        unit_id: card.unit_id,
                        dokLevel: maxDOK || "—",
                        skill: maxEntry ? maxEntry.skill : "—",
                        assessment: card.overall_assessment,
                        term_number: card.term_number,
                        academic_year: card.academic_year,
                        entries: card.entries,
                        educator_comment: card.educator_comment,
                    };
                });

                setTableRows(rows);
                setStudentName(parsed.student || "");
                if (rows.length > 0) {
                    setTerm(rows[0].term_number);
                    setYear(rows[0].academic_year);
                }
                setError("");
            } catch (err) {
                console.error("Error loading data:", err);
                setError("Failed to load assessment data");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [subjectId, subject_id, selectedTerm]);

    const handleUnitClick = (unitName, unitId) => {
        const unitData = tableRows.find(row => row.unit_id === unitId);
        setSelectedUnit(unitData || null);

        // Smooth scroll to UnitDeepDive area after a short delay to allow state update
        setTimeout(() => {
            const deepDiveElement = document.getElementById('unit-deep-dive');
            if (deepDiveElement) {
                deepDiveElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }, 100);
    };

    const getAssessmentColor = (assessment) => {
        switch (assessment?.toLowerCase()) {
            case 'proficient':
                return theme.palette.success.main;
            case 'developing':
                return theme.palette.warning?.main || '#FF9800';
            case 'beginning':
                return theme.palette.error.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    const getDOKLevelColor = (level) => {
        if (!level || level === "—") return theme.palette.text.secondary;
        const colors = {
            1: '#F44336',
            2: '#FF9800',
            3: '#2196F3',
            4: '#4CAF50',
        };
        return colors[level] || theme.palette.primary.main;
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
            <Fade in timeout={800}>
                <Box>
                    {/* Header Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            mb: 4,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${theme.palette.primary.light}15, ${theme.palette.secondary.light}25)`,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                            <Grid item xs={12} sm={8}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    {/* <SchoolIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} /> */}
                                    <Typography
                                        variant={isMobile ? "h5" : "h4"}
                                        fontWeight="bold"
                                        color="primary"
                                        sx={{
                                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}
                                    >
                                        {subject}
                                    </Typography>
                                </Stack>

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={{ xs: 1, sm: 3 }}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <PersonIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                                        <Typography variant="body1" color="text.secondary">
                                            <strong>{studentName}</strong>
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CalendarIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                                        <Typography variant="body1" color="text.secondary">
                                            Term <strong>{term}</strong> • <strong>{year}</strong>
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() =>
                                        navigate("/student-dashboard", {
                                            state: { studentId, fullName, pin },
                                        })
                                    }
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1.5,
                                        px: 3,
                                        boxShadow: '0 4px 12px rgba(112, 130, 56, 0.3)',
                                        '&:hover': {
                                            boxShadow: '0 6px 16px rgba(112, 130, 56, 0.4)',
                                            transform: 'translateY(-1px)'
                                        },
                                        transition: 'all 0.3s ease',
                                        minWidth: { sm: 'auto' }
                                    }}
                                >
                                    Back to Subjects
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Assessment Summary */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 4,
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            background: theme.palette.background.paper,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 60
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                textAlign: { xs: 'center', sm: 'left' }
                            }}
                        >
                            <AssessmentIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
                            The following assessment is for <strong>{studentName}</strong> for{" "}
                            <strong>Term {term}</strong> in <strong>{year}</strong>
                        </Typography>
                    </Paper>

                    {/* Units Table/Cards */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden'
                        }}
                    >
                        {/* Desktop/Tablet Table View */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.primary.light + '15' }}>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <SchoolIcon sx={{ fontSize: 20 }} />
                                                    Unit Name
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                                                    DOK Level Achieved
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <InfoIcon sx={{ fontSize: 20 }} />
                                                    Skill Description
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <AssessmentIcon sx={{ fontSize: 20 }} />
                                                    Overall Assessment
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableRows.map((row, index) => (
                                            <Slide direction="up" in timeout={300 + index * 100} key={index}>
                                                <TableRow
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.action.hover,
                                                            transform: 'scale(1.01)',
                                                            transition: 'all 0.2s ease'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Card
                                                            variant="outlined"
                                                            sx={{
                                                                border: `2px solid ${theme.palette.primary.light}`,
                                                                borderRadius: 2,
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    borderColor: theme.palette.primary.main,
                                                                    boxShadow: '0 4px 16px rgba(112, 130, 56, 0.2)',
                                                                    transform: 'translateY(-2px)'
                                                                }
                                                            }}
                                                        >
                                                            <CardActionArea
                                                                onClick={() => handleUnitClick(row.unit, row.unit_id)}
                                                                sx={{ p: 2 }}
                                                            >
                                                                <CardContent sx={{ p: '0 !important' }}>
                                                                    <Typography
                                                                        fontWeight="bold"
                                                                        color="primary"
                                                                        variant="h6"
                                                                        sx={{
                                                                            textAlign: 'center',
                                                                            fontSize: '1.1rem'
                                                                        }}
                                                                    >
                                                                        {row.unit}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            display: 'block',
                                                                            textAlign: 'center',
                                                                            mt: 0.5
                                                                        }}
                                                                    >
                                                                        Click to view details
                                                                    </Typography>
                                                                </CardContent>
                                                            </CardActionArea>
                                                        </Card>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Chip
                                                            label={row.dokLevel === "—" ? "Not Assessed" : `DOK ${row.dokLevel}`}
                                                            sx={{
                                                                backgroundColor: getDOKLevelColor(row.dokLevel) + '20',
                                                                color: getDOKLevelColor(row.dokLevel),
                                                                fontWeight: 'bold',
                                                                fontSize: '0.9rem',
                                                                py: 1
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                maxWidth: 250,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {row.skill}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Chip
                                                            label={row.assessment}
                                                            sx={{
                                                                backgroundColor: getAssessmentColor(row.assessment) + '20',
                                                                color: getAssessmentColor(row.assessment),
                                                                fontWeight: 'bold',
                                                                fontSize: '0.9rem',
                                                                py: 1
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            </Slide>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/* Mobile Card View */}
                        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                            <Grid container spacing={2}>
                                {tableRows.map((row, index) => (
                                    <Slide direction="up" in timeout={300 + index * 100} key={index}>
                                        <Grid item xs={12}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    border: `2px solid ${theme.palette.primary.light}`,
                                                    borderRadius: 2,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        boxShadow: '0 4px 16px rgba(112, 130, 56, 0.2)',
                                                        transform: 'translateY(-2px)'
                                                    }
                                                }}
                                            >
                                                <CardActionArea
                                                    onClick={() => handleUnitClick(row.unit, row.unit_id)}
                                                    sx={{ p: 3 }}
                                                >
                                                    <CardContent sx={{ p: '0 !important' }}>
                                                        {/* Unit Name Header */}
                                                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                                                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                                                                <SchoolIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                                                <Typography
                                                                    fontWeight="bold"
                                                                    color="primary"
                                                                    variant="h6"
                                                                    sx={{ fontSize: '1.1rem' }}
                                                                >
                                                                    {row.unit}
                                                                </Typography>
                                                            </Stack>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: 'block' }}
                                                            >
                                                                Click to view details
                                                            </Typography>
                                                        </Box>

                                                        {/* Unit Details */}
                                                        <Stack spacing={2}>
                                                            {/* DOK Level */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <TrendingUpIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                                                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                                        DOK Level:
                                                                    </Typography>
                                                                </Stack>
                                                                <Chip
                                                                    label={row.dokLevel === "—" ? "Not Assessed" : `DOK ${row.dokLevel}`}
                                                                    sx={{
                                                                        backgroundColor: getDOKLevelColor(row.dokLevel) + '20',
                                                                        color: getDOKLevelColor(row.dokLevel),
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.9rem'
                                                                    }}
                                                                />
                                                            </Box>

                                                            {/* Skill Description */}
                                                            <Box>
                                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                                    <InfoIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                                                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                                        Skill:
                                                                    </Typography>
                                                                </Stack>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        pl: 3,
                                                                        textAlign: 'left',
                                                                        wordWrap: 'break-word',
                                                                        overflowWrap: 'break-word',
                                                                        lineHeight: 1.5
                                                                    }}
                                                                >
                                                                    {row.skill}
                                                                </Typography>
                                                            </Box>

                                                            {/* Overall Assessment */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <AssessmentIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                                                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                                        Assessment:
                                                                    </Typography>
                                                                </Stack>
                                                                <Chip
                                                                    label={row.assessment}
                                                                    sx={{
                                                                        backgroundColor: getAssessmentColor(row.assessment) + '20',
                                                                        color: getAssessmentColor(row.assessment),
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.9rem'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Stack>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    </Slide>
                                ))}
                            </Grid>
                        </Box>
                    </Paper>

                    {/* Unit Deep Dive Section */}
                    {selectedUnit && (
                        <Slide direction="up" in timeout={500}>
                            <Box mt={4} id="unit-deep-dive">
                                <UnitDeepDive unitData={selectedUnit} />
                            </Box>
                        </Slide>
                    )}

                    {/* Empty State */}
                    {!loading && tableRows.length === 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                background: theme.palette.background.paper
                            }}
                        >
                            <SchoolIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No units found for this subject and term
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please check back later or contact your teacher for more information.
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Fade>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Loading detailed unit assessment information..."
                        />
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default UnitDetailAssessmentPage;
